import { ReactElement, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import routes from "constants/routes";
import { formatWheelRimDiamDisplay } from "utils";
import { generatePdfFile, generatePdfFilename } from "utils/pdfUtils";
import { QuoteDetail, QuotationDetailRequest } from "features/quotation/types";
import { quotationListApi, sendSmsApi } from "features/quotation/api/quotationListApi";

/**
 * Custom hook to fetch and manage the details of a quotation.
 * Handles both tire and wheel quote types.
 * @param withPdf - Whether to include PDF data in the request
 * @returns {object} - An object containing the quotation details and helper functions
 */
const useQuotationDetail = (withPdf = false) => {
  const navigate = useNavigate();
  const { quotationId } = useParams();
  const [quotation, setQuotation] = useState<QuoteDetail | null>(null);

  useEffect(() => {
    /**
     * Fetches the quotation detail based on the quotationId from URL params.
     * Updates the state with the first quotation detail if available.
     */
    async function fetchQuotationDetail() {
      if (quotationId) {
        try {
          const queryParams: QuotationDetailRequest = { quote_id: Number(quotationId) };
          const data = (await quotationListApi(queryParams, withPdf)) as unknown as QuoteDetail[];
          if (data.length > 0) {
            setQuotation(data[0]);
          } else if (!withPdf) {
            navigate(routes.invalidRoute);
          } else {
            navigate(`${routes.invalidRoute}?withPdf=PDF`);
          }
        } catch (error) {
          console.error("Error fetching quotation detail:", error);
        }
      }
    }

    fetchQuotationDetail();
  }, [quotationId, withPdf, navigate]);

  /**
   * Uploads PDF to backend (which stores it on S3) and optionally sends SMS.
   * @param pdfElement - The React element to be converted to PDF
   * @param snsSendFlag - 1: SMS送信, 0: PDF出力のみ（S3にアップロードしてURLを返却）
   * @returns S3 public URL of the uploaded PDF, or null if quotation is missing.
   */
  const handleSendSmsWithPdf = useCallback(
    async (pdfElement: ReactElement, snsSendFlag: 0 | 1 = 1) => {
      if (quotation) {
        try {
          // Generate PDF filename
          const filename = generatePdfFilename(
            quotation.quote_no || "unknown",
            quotation.customer_name || "customer",
          );

          // Generate PDF file
          const pdfFile = await generatePdfFile(pdfElement, filename);

          // Upload PDF (and send SMS when flag=1)
          const response = await sendSmsApi({
            quote_id: quotation.quote_id,
            quote_no: quotation.quote_no || String(quotation.quote_id),
            phone_number: quotation.customer_tel_no,
            pdf_file: pdfFile,
            sns_send_flag: snsSendFlag,
          });

          return response;
        } catch (error) {
          console.error("Error sending SMS with PDF:", error);
          throw error;
        }
      }
      return null;
    },
    [quotation],
  );

  // Check if this is a wheel type quotation
  const isWheelType = useMemo(() => quotation?.quote_type === "wheel", [quotation?.quote_type]);

  // Check if this is a tire type quotation
  const isTireType = useMemo(() => quotation?.quote_type === "tire", [quotation?.quote_type]);

  // Get first tire detail (works for both tire and wheel types)
  const firstTireDetail = useMemo(() => {
    if (!quotation) return null;

    if (isWheelType && quotation.tbl_quote_detail_wheel?.[0]) {
      return quotation.tbl_quote_detail_wheel[0].tbl_quote_detail_tire[0] || null;
    }

    if (isTireType && quotation.tbl_quote_detail_tire?.[0]) {
      return quotation.tbl_quote_detail_tire[0];
    }

    return null;
  }, [quotation, isWheelType, isTireType]);

  // Calculate tire size display string
  const tireSizeRender = useMemo(() => {
    if (!firstTireDetail) return "";

    const { midt_tire_width, midt_tire_aspect_ratio, midt_tire_rim_diam } = firstTireDetail;

    if (!midt_tire_width || !midt_tire_aspect_ratio || !midt_tire_rim_diam) return "";

    return `${midt_tire_width}/${midt_tire_aspect_ratio}R${midt_tire_rim_diam}`;
  }, [firstTireDetail]);

  /** ハブ径欄: `mmmy_mmm_hub1` on quote when present, else first tire `midt_tire_rim_diam` */
  const hubRimDisplay = useMemo(() => {
    const fromHub = formatWheelRimDiamDisplay(quotation?.mmmy_mmm_hub1);
    if (fromHub !== "") return fromHub;
    if (!firstTireDetail) return "";
    return formatWheelRimDiamDisplay(firstTireDetail.midt_tire_rim_diam);
  }, [quotation?.mmmy_mmm_hub1, firstTireDetail?.midt_tire_rim_diam]);

  // Calculate car label with model period
  const carLabel = useMemo(() => {
    if (!quotation) return "";

    const brand = `${quotation.brnd_name}${quotation.modl_name}`;
    const period =
      quotation.modl_period_s &&
      quotation.modl_period_e &&
      `${quotation.modl_period_s}～${quotation.modl_period_e}`;

    return `${brand}\n${period}`;
  }, [quotation]);

  return {
    quotation,
    quotationId,
    isWheelType,
    isTireType,
    firstTireDetail,
    tireSizeRender,
    hubRimDisplay,
    carLabel,
    handleSendSmsWithPdf,
  };
};

export default useQuotationDetail;
